/**
 * Предохранитель от шторма событий в Hawk — общий для клиента и сервера.
 *
 * Одна поломка на горячем пути генерирует сотни одинаковых событий в минуту.
 * Без ограничителя это выжигает квоту проекта и превращает ленту в один
 * бесконечный дубль, из-за которого не видно остальных ошибок.
 *
 * Две независимые защиты:
 *  - дедуп по отпечатку: повтор той же ошибки в окне подавляется;
 *  - глобальный потолок в минуту: даже разные ошибки не пролезут пачкой.
 */
export interface HawkThrottleOptions {
	/** Максимум отправок в минуту (все ошибки суммарно). */
	maxPerMinute: number;
	/** Окно подавления повторов одного отпечатка, мс. */
	dedupeWindowMs: number;
}

/**
 * Создаёт функцию-фильтр: `true` — событие можно отправлять, `false` — гасим.
 * Состояние живёт в замыкании, поэтому у каждого приложения оно своё.
 */
export function createHawkThrottle(
	options: HawkThrottleOptions,
): (fingerprint: string, now?: number) => boolean {
	const seenAt = new Map<string, number>();
	let windowStartedAt = 0;
	let sentInWindow = 0;

	return function allow(fingerprint: string, now = Date.now()): boolean {
		if (now - windowStartedAt >= 60_000) {
			windowStartedAt = now;
			sentInWindow = 0;
			for (const [key, at] of seenAt) {
				if (now - at > options.dedupeWindowMs) seenAt.delete(key);
			}
		}

		if (sentInWindow >= options.maxPerMinute) return false;

		const previous = seenAt.get(fingerprint);
		if (previous !== undefined && now - previous < options.dedupeWindowMs) {
			return false;
		}

		seenAt.set(fingerprint, now);
		sentInWindow += 1;
		return true;
	};
}
